import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import {
    CreditCollateralTable,
    CreditCustodianEntity,
    CreditForm,
    CreditKeyMetrics,
    CreditLoansTable,
    CreditMarginOverTimeChart
} from 'src/app/shared/state/credits/credits.model';
import { CreditsStore } from '../../../../shared/state/credits/credits.store';
import { formatDate } from '@angular/common';
import { CustodianEntityService } from './custodianEntities.service';
import { NumberUtils } from '../../../../shared/utils/number-utils';

@Injectable({ providedIn: 'root' })
export class CreditsService {
    baseApiUrl = `${this.config.get().consumerBaseUrl}/v1/consume_credit_data`;

    constructor(
        private config: AppConfigQuery,
        private creditsStore: CreditsStore,
        private custodianEntitiesService: CustodianEntityService,
        private http: HttpClient
    ) {}

    private getApiUrl(path: string, creditForm: CreditForm): string {
        const formattedDate = formatDate(creditForm.date, 'yyyy-MM-dd', 'en-US');
        const params = {
            value_date: formattedDate,
            custodian: creditForm.custodian,
            entity: creditForm.entity
        };

        let apiUrl = `${this.baseApiUrl}/${path}`;

        Object.keys(params).forEach((key, index) => {
            const prefix = index === 0 ? '?' : '&';
            apiUrl += `${prefix}${key}=${params[key]}`;
        });

        return apiUrl;
    }

    getCollateralTable(creditForm: CreditForm): Observable<CreditCollateralTable[]> {
        const apiUrl = this.getApiUrl('collateral', creditForm);

        return this.http.get<any>(apiUrl).pipe(
            map((response) => {
                if (
                    response.results.client1.credit_collateral &&
                    response.results.client1.credit_collateral.length > 0
                ) {
                    return response.results.client1.credit_collateral.map((res: any) => {
                        return {
                            accountNumber: res.accountNumber?.toString() || '',
                            baseCcy: res.baseCcy,
                            ccy: res.ccy,
                            ccyRate: res.ccyRate ? Number(res.ccyRate.toFixed(2)) : 0,
                            collateralClass: res.collateralClass,
                            collateralValue: res.collateralValue
                                ? NumberUtils.formatNumberToSeperated(res.collateralValue)
                                : 0,
                            marginRate: res.marginRate
                                ? Number((res.marginRate * 100).toFixed(2))
                                : 0,
                            marketExposureNet: res.marketExposureNet
                                ? NumberUtils.formatNumberToSeperated(
                                      res.marketExposureNet
                                  )
                                : 0,
                            name: res.name,
                            price: res.price ? Number(res.price.toFixed(2)) : 0,
                            quantity: res.quantity ? Number(res.quantity.toFixed(2)) : 0,
                            ticker: res.ticker,
                            type: res.type,
                            weight: res.weight ? Number(res.weight * 100) : 0
                        };
                    });
                } else {
                    return of([]);
                }
            })
        );
    }

    getKeyMetrics(creditForm: CreditForm): Observable<CreditKeyMetrics> {
        const apiUrl = this.getApiUrl('key_metric', creditForm);

        return this.http.get<any>(apiUrl).pipe(
            map((response) => {
                if (
                    response.results.client1.credit_key_metric &&
                    response.results.client1.credit_key_metric.length > 0
                ) {
                    return response.results.client1.credit_key_metric.map((res: any) => {
                        return {
                            available: res.available,
                            collateralValue: res.collateralValue,
                            currency: res.currency,
                            custodian: res.custodian,
                            entity: creditForm.entity,
                            exposureLimit: res.exposureLimit,
                            marketExposureNet: res.marketExposureNet,
                            netMargin: res.netMargin,
                            statusAsOf: res.statusAsOf.replace(' ', '<br/>'),
                            utilized: res.utilized
                        };
                    });
                } else {
                    return of(undefined);
                }
            })
        );
    }

    getKeyMetricsForDashboard(): Observable<any> {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        const apiUrl =
            this.baseApiUrl +
            '/dashboard?' +
            'value_date=' +
            yesterday.toISOString().split('T')[0];

        return this.http.get<any>(apiUrl);
    }

    getLoansTable(creditForm: CreditForm): Observable<CreditLoansTable[]> {
        const apiUrl = this.getApiUrl('loan', creditForm);

        return this.http.get<any>(apiUrl).pipe(
            map((response) => {
                if (
                    response.results.client1.credit_loan &&
                    response.results.client1.credit_loan.length > 0
                ) {
                    return response.results.client1.credit_loan.map((res: any) => {
                        const ret = {
                            accountNumber: res.accountNumber?.toString() || '',
                            baseCcy: res.baseCcy,
                            ccy: res.ccy,
                            ccyRate: res.ccyRate ? Number(res.ccyRate.toFixed(2)) : 0,
                            collateralClass: res.collateralClass,
                            name: res.name,
                            rateFee: res.rateFee ? Number(res.rateFee.toFixed(2)) : 0,
                            type: res.type,
                            utilized: res.utilized
                                ? NumberUtils.formatNumberToSeperated(res.utilized)
                                : 0,
                            weight: res.weight ? Number(res.weight * 100) : 0
                        };
                        return ret;
                    });
                } else {
                    return of([]);
                }
            })
        );
    }

    getMarginOvertimeChart(
        creditForm: CreditForm
    ): Observable<CreditMarginOverTimeChart[]> {
        const formattedToDate = formatDate(creditForm.date, 'yyyy-MM-dd', 'en-US');
        const formattedFromDate = '2000-01-01'; // Arbitrary date way back in the past

        const apiUrl =
            `${this.baseApiUrl}/margin_over_time` +
            `?from_date=${formattedFromDate}&to_date=${formattedToDate}&custodian=${creditForm.custodian}&entity=${creditForm.entity}`;

        return this.http.get<any>(apiUrl).pipe(
            map((response) =>
                response.map((item: CreditMarginOverTimeChart) => {
                    return {
                        ...item,
                        data: item.data.map(([timestamp, value]) => [
                            timestamp * 1000,
                            value
                        ])
                    };
                })
            )
        );
    }
}
